import { Injectable, EventEmitter } from '@angular/core';
import { Bi } from '@entities/bi.class';
import { BiService } from '@services/api/bi.service';
import { ombreDossier } from '@services/core/ombre-dossier';
import { fournirTailleDossier, noterNotification } from '@services/core/gel-contexte';

@Injectable({
	providedIn: 'root'
})
export class BiState {
	protected bi: Bi | null;

	public biChange = new EventEmitter<any>();
	public resetForm = new EventEmitter<any>();
	public reloadPhrases = new EventEmitter<any>();
	public reloadColors = new EventEmitter<any>();
	public photosReady = new EventEmitter<void>(); // Émis quand les photos sont chargées depuis IndexedDB

	constructor(
		private biService: BiService
	) {
		// Taille du dossier en mémoire, jointe aux gels (perf.gel).
		fournirTailleDossier((): Record<string, number> => {
			const r: any = this.bi?.rapport;
			if (!r) return {};
			return {
				id: Number(this.bi?.id) || 0,
				phrases: r.phrases?.length ?? 0,
				photos: r.photos?.length ?? 0,
				investigations: r.investigations?.length ?? 0,
				logements: Array.isArray(r.tableauDegat) ? r.tableauDegat.length : 0,
			};
		});
	}

	get(): Bi | null {
		return this.bi;
	}

	async set(id: number, emit: boolean = true) {
		let bi = await this.biService.get(id, false);
		bi.isSynced = true;
		
		this.bi = bi;
		if(emit) {
			this.biChange.emit(this.bi);
		}
		// Phase 1 du stockage unitaire : comparaison différée avec le monolithe (télémétrie).
		ombreDossier.comparerPlusTard(id);
	}

	reloadBi(bi: Bi, synced:boolean = false, resetForm:boolean = false, reloadPhrases: boolean = false, reloadColors: boolean = false) {
		this.bi = bi;
		this.bi.isSynced = synced;

		this.biService.localPatchEntity(this.bi);
		this.notifier(bi, synced, resetForm, reloadPhrases, reloadColors);
	}

	/**
	 * Comme reloadBi, SANS réécrire tout le dossier dans la copie locale : pour les appelants
	 * qui l'ont déjà mise à jour de façon ciblée (BiService.majPhotosLocales / majPhraseLocale).
	 * Réécrire le dossier entier à chaque action figeait les postes lents sur les gros
	 * dossiers (voir atlas-v2-stockage-local).
	 */
	notifier(bi: Bi, synced:boolean = false, resetForm:boolean = false, reloadPhrases: boolean = false, reloadColors: boolean = false) {
		this.bi = bi;
		this.bi.isSynced = synced;
		// Durée synchrone du rafraîchissement (abonnés compris) : contexte des gels (perf.gel).
		const debut = performance.now();
		if(reloadColors) {
			this.reloadColors.emit(this.bi);
		}
		else if(reloadPhrases) {
			this.reloadPhrases.emit(this.bi);
		}
		else if(resetForm) {
			this.resetForm.emit(this.bi);
		}
		else {
			this.biChange.emit(this.bi);
		}
		noterNotification(debut, performance.now());
	}

	syncBi() {
		if(this.bi == null) {
			return;
		}
		if(!this.bi.isSynced) {
			this.bi.isSynced = true;
			this.biChange.emit(this.bi);
		}
	}

	clearBi() {
		this.bi = null;
		this.biChange.emit(null);
	}
}